export const IMAGE_JOB_POLL_INTERVAL_MS = 2000
export const IMAGE_JOB_SLOW_POLL_INTERVAL_MS = 5000
// Poll every 2 s for the first 2 minutes, then every 5 s.
export const IMAGE_JOB_FAST_POLL_WINDOW_MS = 2 * 60 * 1000
// 22 min: the worker gives each image provider call 5 minutes
// (WithImageTimeout(5*time.Minute) in apps/shared/pkg/workerapp/app.go) and
// requeues a timed-out job up to 3 more times (defaultMaxRetries = 3 in
// apps/studio/workers/aijobs/runner.go), so four attempts of up to 5 minutes
// plus queue pickup and retry delays.
export const IMAGE_JOB_MAX_WAIT_MS = 22 * 60 * 1000

export const IMAGE_JOB_MAX_READ_FAILURES = 3
export const IMAGE_JOB_READ_TIMEOUT_MS = 30 * 1000

export function canResumeImageGenerationJob(job) {
  return Boolean(job?.id && !job.applied && !(job.status === 'failed' && job.terminal))
}

export function restoreImageGenerationJob(job) {
  if (!job?.id) return null
  // Legacy completed drafts may already have attached or dismissed the image.
  // Only an explicit unapplied flag is permission to apply that result again.
  const restored = { ...job, applied: job.applied ?? (job.status === 'completed') }
  if (!canResumeImageGenerationJob(restored)) return restored
  return {
    ...restored,
    // Older drafts used failed for interrupted polling, so verify that ID first.
    status: job.status === 'failed' ? 'pending' : job.status,
    observation: 'paused',
    error: 'Resume to check your existing image.',
  }
}

function abortError() {
  return new DOMException('Image observation stopped', 'AbortError')
}

// Stop waiting locally without cancelling the durable server job. Read promises
// remain handled even when a deadline or navigation wins the race.
function waitForRead(read, timeoutMs, signal) {
  return new Promise((resolve, reject) => {
    const finish = (callback, value) => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', onAbort)
      callback(value)
    }
    const onAbort = () => finish(reject, abortError())
    const timer = setTimeout(() => finish(reject, new Error('Image status request timed out')), timeoutMs)
    if (signal?.aborted) {
      onAbort()
      return
    }
    signal?.addEventListener('abort', onAbort, { once: true })
    Promise.resolve().then(read).then(
      (value) => finish(resolve, value),
      (error) => finish(reject, error),
    )
  })
}

function waitForPoll(delay, signal) {
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer)
      reject(abortError())
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, delay)
    if (signal?.aborted) onAbort()
    else signal?.addEventListener('abort', onAbort, { once: true })
  })
}

// A retry observes the same durable job. Each observation window is bounded;
// startedAt remains the original creation time, including after a draft restore.
export async function runImageGenerationJob({ createJob, getJob, onStatus, job: existingJob, signal }) {
  if (signal?.aborted) throw abortError()
  const started = existingJob?.id ? existingJob : (await createJob())?.job
  if (signal?.aborted) throw abortError()
  const jobId = started?.id
  if (!jobId) throw new Error('Image generation did not return a job ID')
  const startedAt = started.startedAt ?? started.created_at ?? Date.now()
  const observingAt = Date.now()
  let readFailures = 0
  const report = (job) => onStatus?.({
    id: jobId,
    status: job.status || 'processing',
    attempt: job.attempt,
    startedAt,
    terminal: job.status === 'failed',
    observation: 'polling',
    error: null,
  })
  report({ ...started, status: started.status === 'failed' && !started.terminal ? 'pending' : started.status || 'pending' })

  for (;;) {
    let response
    try {
      response = await waitForRead(
        () => getJob(jobId),
        Math.min(IMAGE_JOB_READ_TIMEOUT_MS, Math.max(0, IMAGE_JOB_MAX_WAIT_MS - (Date.now() - observingAt))),
        signal,
      )
      if (!response?.job?.status) throw new Error('Image status is unavailable')
      readFailures = 0
    } catch {
      if (signal?.aborted) throw abortError()
      if (Date.now() - observingAt >= IMAGE_JOB_MAX_WAIT_MS) break
      readFailures += 1
      if (readFailures >= IMAGE_JOB_MAX_READ_FAILURES) {
        throw new Error('Image status is unavailable. Resume to check the same image again.')
      }
      await waitForPoll(Math.min(IMAGE_JOB_POLL_INTERVAL_MS * 2 ** (readFailures - 1), IMAGE_JOB_MAX_WAIT_MS - (Date.now() - observingAt)), signal)
      continue
    }
    if (signal?.aborted) throw abortError()
    const job = response.job
    report(job)
    if (job.status === 'failed') {
      const detail = typeof job.error?.message === 'string' ? job.error.message.trim() : ''
      throw new Error(detail || (job.error?.retryable ? 'Image generation failed. Try again.' : 'Image generation failed.'))
    }
    if (job.status === 'completed') return job
    const elapsed = Date.now() - observingAt
    if (elapsed >= IMAGE_JOB_MAX_WAIT_MS) break
    const age = Date.now() - (typeof startedAt === 'number' ? startedAt : Date.parse(startedAt))
    const interval = age < IMAGE_JOB_FAST_POLL_WINDOW_MS ? IMAGE_JOB_POLL_INTERVAL_MS : IMAGE_JOB_SLOW_POLL_INTERVAL_MS
    await waitForPoll(Math.min(interval, IMAGE_JOB_MAX_WAIT_MS - elapsed), signal)
  }
  throw new Error('Image generation is still running. Resume to check the same image again.')
}

// Server errors arrive either as { error: "message" } or as the AI error shape
// { error: { code, message, retryable } }.
export function imageGenerationErrorMessage(error, fallback = 'Failed to generate image') {
  const serverError = error?.response?.data?.error
  if (typeof serverError === 'string' && serverError.trim()) return serverError
  if (typeof serverError?.message === 'string' && serverError.message.trim()) return serverError.message
  if (typeof error?.message === 'string' && error.message.trim()) return error.message
  return fallback
}
